// Shared Clerk appearance config. Applied at the ClerkProvider level so it
// cascades to every Clerk surface (SignIn, SignUp, UserProfile, UserButton,
// PricingTable, etc.) — keeps the auth flows visually identical to the rest
// of the Steerly workbench.
//
// Color values mirror the DARK workbench palette in apps/web/styles.css
// (:root[data-theme="dark"]: --bg #0e0e10, --panel #161618, --panel-soft
// #1c1c1f, --ink #e6e7ea, --muted #8a8e96, --line #26272c, --accent #3b82f6,
// --accent-strong #5a96ff). They are hardcoded here because Clerk reads
// appearance.variables at component-mount time and does not resolve CSS vars.
// The pre-app auth surface is intentionally always dark (see index.html's
// theme bootstrap), so a single dark config — not a per-theme switch — is correct.
// The distinctive sign-in styling lives in styles.css scoped to `.auth-stage`;
// this config is the shared baseline every Clerk surface inherits.

export const CLERK_APPEARANCE = {
  variables: {
    colorBackground: "#161618",
    colorInputBackground: "#0e0e10",
    colorText: "#e6e7ea",
    colorTextSecondary: "#8a8e96",
    colorTextOnPrimaryBackground: "#ffffff",
    colorPrimary: "#3b82f6",
    colorNeutral: "#26272c",
    colorDanger: "#f06b62",
    colorSuccess: "#1fbf75",
    borderRadius: "10px",
    fontFamily: "inherit",
  },
  elements: {
    card: {
      backgroundColor: "#161618",
      borderColor: "#26272c",
      boxShadow: "0 16px 40px rgba(0, 0, 0, 0.45)",
    },
    rootBox: {
      width: "100%",
    },
    headerTitle: {
      color: "#e6e7ea",
    },
    headerSubtitle: {
      color: "#8a8e96",
    },
    socialButtonsBlockButton: {
      backgroundColor: "#0e0e10",
      borderColor: "#26272c",
      color: "#e6e7ea",
    },
    socialButtonsBlockButton__hover: {
      backgroundColor: "#1c1c1f",
    },
    dividerLine: {
      backgroundColor: "#26272c",
    },
    dividerText: {
      color: "#8a8e96",
    },
    formFieldLabel: {
      color: "#e6e7ea",
    },
    formFieldInput: {
      backgroundColor: "#0e0e10",
      borderColor: "#26272c",
      color: "#e6e7ea",
    },
    formButtonPrimary: {
      backgroundColor: "#3b82f6",
      color: "#ffffff",
      textTransform: "none",
      boxShadow: "none",
    },
    formButtonPrimary__hover: {
      backgroundColor: "#5a96ff",
    },
    footerActionText: {
      color: "#8a8e96",
    },
    footerActionLink: {
      color: "#5a96ff",
    },
    identityPreviewEditButton: {
      color: "#5a96ff",
    },

    // ── UserButton account popover ──────────────────────────────────────
    // The default `card` style themes the popover surface, but the action
    // rows, identity text, and footer need explicit theming or they render
    // as low-contrast defaults over the dark workbench (the "bad UI" the
    // popover showed). Match the workbench surfaces/accent exactly.
    userButtonPopoverCard: {
      backgroundColor: "#161618",
      borderColor: "#26272c",
      border: "1px solid #26272c",
      boxShadow: "0 16px 44px rgba(0, 0, 0, 0.55)",
      borderRadius: "12px",
    },
    userButtonPopoverMain: {
      backgroundColor: "#161618",
    },
    userButtonPopoverActions: {
      backgroundColor: "#161618",
    },
    userButtonPopoverActionButton: {
      color: "#e6e7ea",
      borderRadius: "8px",
    },
    // Clerk targets element states with the `__hover` suffix, NOT a CSS
    // `:hover` pseudo-class key (cf. formButtonPrimary__hover above).
    userButtonPopoverActionButton__hover: {
      backgroundColor: "#1c1c1f",
      color: "#fafafa",
    },
    userButtonPopoverActionButtonText: {
      color: "#e6e7ea",
    },
    userButtonPopoverActionButtonIcon: {
      color: "#8a8e96",
    },
    userButtonPopoverFooter: {
      backgroundColor: "#0e0e10",
      borderTopColor: "#26272c",
    },
    userPreview: {
      backgroundColor: "transparent",
    },
    userPreviewMainIdentifier: {
      color: "#fafafa",
      fontWeight: "600",
    },
    userPreviewSecondaryIdentifier: {
      color: "#8a8e96",
    },
    avatarBox: {
      borderRadius: "10px",
    },
  },
} as const;
